import * as THREE from "three";

/**
 * A world-locked glass panel, styled after Horizon OS surfaces.
 *
 * Three decisions carried over from Probe 1, all of them earned the hard way:
 *
 *   1. Body-locked, never head-locked. Head-locked content is drawn into the
 *      projection layer, which timewarp reprojects to the newest head pose —
 *      world content is corrected by that, head-locked content is dragged by it
 *      and visibly swims. It also matches Meta's own guidance that panels live
 *      in space rather than on your face.
 *   2. Repaint is throttled. Live text changes every frame, and repainting a
 *      1024x512 canvas at 90 Hz cost ~3 ms/frame when measured.
 *   3. Pose is read straight from matrixWorld. In an immersive session three
 *      never writes the XR camera's position/quaternion, and getWorldDirection()
 *      internally rebuilds matrixWorld from those stale values, handing back the
 *      rig's transform instead of the head's (three.js #19891, #18448, #23597).
 */

const WIDTH_M = 0.58;
const HEIGHT_M = 0.30;
const CANVAS_W = 1024;
const CANVAS_H = 530;
const DISTANCE = 1.0;   // Meta's guidance for a large readable surface
const DROP = 0.34;      // sits below the line of sight
const DEAD_ZONE = THREE.MathUtils.degToRad(24);
const FOLLOW_RATE = 3.0;
const REPAINT_MS = 400;
const UP = new THREE.Vector3(0, 1, 0);

export interface PanelContent {
  readonly eyebrow: string;
  readonly title: string;
  readonly rows: readonly (readonly [string, string])[];
  readonly footer: string;
  readonly accent?: string;
}

export class Panel {
  readonly group = new THREE.Group();
  readonly #canvas = document.createElement("canvas");
  readonly #ctx: CanvasRenderingContext2D;
  readonly #texture: THREE.Texture;
  readonly #viewPosition = new THREE.Vector3();
  readonly #forward = new THREE.Vector3();
  #yaw = 0;
  #targetYaw = 0;
  #placed = false;
  #dirty = true;
  #lastPaint = -Infinity;
  #uploading = false;
  #content: PanelContent = {
    eyebrow: "",
    title: "",
    rows: [],
    footer: ""
  };

  constructor() {
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = CANVAS_H;
    const ctx = this.#canvas.getContext("2d");
    if (ctx === null) {
      throw new Error("2D context unavailable");
    }
    this.#ctx = ctx;

    this.#texture = new THREE.Texture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4;
    // Orientation is baked into an ImageBitmap in #upload rather than left to
    // texture.flipY. Spark issues its own raw GL texture uploads, which leaves
    // three's cached UNPACK_FLIP_Y_WEBGL state stale — so a canvas texture
    // inherits whichever flip was last set, and the panel came out upright or
    // upside down depending purely on load ordering. three never applies flipY
    // to an ImageBitmap, so encoding it in the bitmap makes it deterministic.
    this.#texture.flipY = false;

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(WIDTH_M, HEIGHT_M),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false,
        depthWrite: false
      })
    );
    mesh.renderOrder = 1000;
    this.group.add(mesh);
  }

  set(content: PanelContent): void {
    this.#content = content;
    this.#dirty = true;
  }

  update(camera: THREE.Camera, deltaSeconds: number): void {
    const m = camera.matrixWorld.elements;
    this.#viewPosition.setFromMatrixPosition(camera.matrixWorld);
    this.#forward.set(-m[8]!, -m[9]!, -m[10]!);
    this.#forward.y = 0;
    if (this.#forward.lengthSq() < 1e-6) {
      this.#forward.set(0, 0, -1);
    }
    this.#forward.normalize();

    const viewYaw = Math.atan2(this.#forward.x, this.#forward.z);
    if (!this.#placed) {
      this.#yaw = viewYaw;
      this.#targetYaw = viewYaw;
      this.#placed = true;
    } else {
      let delta = viewYaw - this.#targetYaw;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      if (Math.abs(delta) > DEAD_ZONE) {
        this.#targetYaw = viewYaw - Math.sign(delta) * DEAD_ZONE;
      }
    }
    let toTarget = this.#targetYaw - this.#yaw;
    toTarget = Math.atan2(Math.sin(toTarget), Math.cos(toTarget));
    this.#yaw += toTarget * (1 - Math.exp(-FOLLOW_RATE * deltaSeconds));

    const sin = Math.sin(this.#yaw);
    const cos = Math.cos(this.#yaw);
    this.group.position.set(
      this.#viewPosition.x + sin * DISTANCE,
      this.#viewPosition.y - DROP,
      this.#viewPosition.z + cos * DISTANCE
    );
    this.group.quaternion.setFromAxisAngle(UP, Math.atan2(-sin, -cos));

    const now = performance.now();
    if (this.#dirty && now - this.#lastPaint >= REPAINT_MS) {
      this.#lastPaint = now;
      this.#dirty = false;
      this.#paint();
      void this.#upload();
    }
  }

  recenter(): void {
    this.#placed = false;
  }

  #paint(): void {
    const ctx = this.#ctx;
    const { eyebrow, title, rows, footer } = this.#content;
    const accent = this.#content.accent ?? "#6ee7ff";

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Dark translucent glass with a soft vertical lift, per the Horizon OS look.
    const bg = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    bg.addColorStop(0, "rgba(16, 24, 34, 0.92)");
    bg.addColorStop(1, "rgba(9, 14, 21, 0.94)");
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.roundRect(6, 6, CANVAS_W - 12, CANVAS_H - 12, 34);
    ctx.fill();
    ctx.strokeStyle = "rgba(140, 200, 225, 0.22)";
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = accent;
    ctx.font = "600 24px ui-monospace, Menlo, monospace";
    ctx.letterSpacing = "3px";
    ctx.fillText(eyebrow.toUpperCase(), 46, 62);
    ctx.letterSpacing = "0px";

    ctx.fillStyle = "#f2fbff";
    ctx.font = "600 46px system-ui, -apple-system, sans-serif";
    ctx.fillText(title, 46, 120);

    let y = 186;
    for (const [label, value] of rows) {
      ctx.fillStyle = "rgba(178, 205, 222, 0.85)";
      ctx.font = "400 27px ui-monospace, Menlo, monospace";
      ctx.fillText(label, 46, y);
      ctx.fillStyle = "#eaf7ff";
      ctx.font = "500 29px ui-monospace, Menlo, monospace";
      ctx.fillText(value, 330, y);
      y += 46;
    }

    ctx.fillStyle = "rgba(150, 180, 198, 0.72)";
    ctx.font = "400 24px system-ui, -apple-system, sans-serif";
    ctx.fillText(footer, 46, CANVAS_H - 44);

  }

  /**
   * Uploads the painted canvas with its orientation fixed in the bitmap.
   * `imageOrientation: "flipY"` compensates for canvas 2D being top-left origin
   * while GL sampling is bottom-left, without depending on GL unpack state that
   * another renderer in the same context may have changed.
   */
  async #upload(): Promise<void> {
    if (this.#uploading) {
      return;
    }
    this.#uploading = true;
    try {
      const bitmap = await createImageBitmap(this.#canvas, {
        imageOrientation: "flipY"
      });
      this.#texture.image = bitmap;
      this.#texture.needsUpdate = true;
    } catch {
      // Fall back to the canvas itself; orientation may vary but the panel
      // still renders rather than going blank.
      this.#texture.image = this.#canvas;
      this.#texture.needsUpdate = true;
    } finally {
      this.#uploading = false;
    }
  }
}
